import { useEffect, useState, useRef, useCallback } from "react";
import {
  GAMENIGHTTV_CAST_CHANNEL,
  RECEIVER_TIMEOUT_SEC,
  TESTING_ENABLED,
} from "../util/consts";
import { GTVMessage } from "../util/types";

declare const cast: any; // <-- important bit. you are using google's code here

export function useCastReceiver(
  hostAuthorized: boolean,
  socketConnected: boolean,
  sendSocketMessage: (msg: GTVMessage) => void | Promise<void>,
): {
  castConnected: boolean;
  sendToCastSender: (message: GTVMessage) => void | Promise<void>;
  disableCasting: boolean;
  ping: number | null;
  senderSessionId: string | null;
  playMedia: (src: string) => void;
} {
  const [castReceiverLoaded, setCastReceiverLoaded] = useState(false);
  const [castStatus, setCastStatus] = useState("DISCONNECTED");
  const queryParams = new URLSearchParams(window.location.search);
  const disableCasting = queryParams.get("disableCasting") === "true";
  const [senderSessionId, setSenderSessionId] = useState<string | null>(null);
  const [ping, setPing] = useState<number | null>(null);
  const isFirstRender = useRef(true);
  const [mediaDiabled, setMediaDisabled] = useState(false);
  const castReceiverLog = useCallback(
    async (type: "Error" | "Info" | "Warning", message: string) => {
      switch (type) {
        case "Error":
          console.error("CastReceiver: " + message);
          break;
        case "Warning":
          console.warn("CastReceiver: " + message);
          break;
        default:
          console.log("CastReceiver: " + message);
      }
      // Uncomment to enable cast receiver logs to be sent to the server for debugging
      await sendSocketMessage({
        Type: "CastReceiverLog",
        Payload: {
          Level: type,
          Message: message,
        },
      });
    },
    [sendSocketMessage],
  );

  const sendToCastSender = useCallback(
    async (message: GTVMessage) => {
      if (castStatus !== "CONNECTED") {
        await castReceiverLog(
          "Warning",
          "Cannot send message, not connected to sender",
        );
        return;
      }
      try {
        const context = cast.framework.CastReceiverContext.getInstance();
        context.sendCustomMessage(
          GAMENIGHTTV_CAST_CHANNEL,
          JSON.stringify(message),
        );
        await castReceiverLog(
          "Info",
          "Sent cast message to sender: " + JSON.stringify(message),
        );
      } catch (error) {
        await castReceiverLog(
          "Error",
          "Error sending message to sender: " + (error as Error)?.message ||
            "Unknown error",
        );
      }
    },
    [castStatus, castReceiverLog],
  );
  const castReceiverMessageHandler = useCallback(
    async (msg: GTVMessage) => {
      if (senderSessionId && msg && msg.SessionId !== senderSessionId) {
        return; // Ignore cast messages from other senders
      }
      if (
        msg &&
        msg.Type === "ConnectHost" &&
        msg.SessionId &&
        msg.SessionId.length > 0
      ) {
        setSenderSessionId(msg.SessionId);
      } else if (msg && msg.Type === "Ping") {
        const currentTime = Date.now();
        if (msg.Payload && msg.Payload.Timestamp) {
          setPing(currentTime - msg.Payload.Timestamp);
        } else {
          await castReceiverLog(
            "Warning",
            "Ping message missing timestamp payload",
          );
        }
      }
    },
    [setSenderSessionId, senderSessionId, castReceiverLog],
  );
  const configureReceiverCasting = useCallback(async () => {
    try {
      await castReceiverLog("Info", "Setting up Google Cast for Host...");
      const options = new cast.framework.CastReceiverOptions();
      options.disableIdleTimeout = true;
      options.maxInactivity = RECEIVER_TIMEOUT_SEC;
      const context = cast.framework.CastReceiverContext.getInstance();
      context.addEventListener(
        cast.framework.system.EventType.SENDER_CONNECTED,
        async () => {
          setCastStatus("CONNECTED");
        },
      );
      context.addEventListener(
        cast.framework.system.EventType.SENDER_DISCONNECTED,
        async () => {
          setCastStatus("DISCONNECTED");
        },
      );
      context.addEventListener(
        cast.framework.system.EventType.READY,
        async () => {
          setCastStatus("READY");
        },
      );
      context.addCustomMessageListener(
        GAMENIGHTTV_CAST_CHANNEL,
        async (customEvent: any) => {
          try {
            await castReceiverLog(
              "Info",
              "Received custom cast message: " +
                JSON.stringify(customEvent.data),
            );
            await castReceiverMessageHandler(customEvent.data as GTVMessage);
            const playerManager = context.getPlayerManager();
            if (!isNaN(playerManager.getDurationSec())) {
              playerManager.play();
            }
          } catch (error) {
            const msg = (error as Error)?.message || "Unknown error";
            await castReceiverLog(
              "Error",
              "Error handling custom cast message: " + msg,
            );
          }
        },
      );
      const playerManager = context.getPlayerManager();
      playerManager.setMessageInterceptor(
        cast.framework.messages.MessageType.LOAD,
        (loadRequestData: any) => {
          console.log("LOAD intercepted", loadRequestData);
          return loadRequestData; // allow the load to proceed
        },
      );
      context.start(options);
      await castReceiverLog("Info", "Google Cast Receiver Context started");
    } catch (error) {
      await castReceiverLog("Error", "Error configuring Google Cast: " + error);
    }
  }, [castReceiverMessageHandler, setCastStatus, castReceiverLog]);
  const loadGoogleCastReceiverFramework = useCallback(async () => {
    if (castReceiverLoaded) {
      await castReceiverLog(
        "Info",
        "Google Cast Receiver Framework already loaded",
      );
      configureReceiverCasting();
      return;
    }
    // Load Google Cast API Framework First
    const script = document.createElement("script");
    script.src =
      "//www.gstatic.com/cast/sdk/libs/caf_receiver/v3/cast_receiver_framework.js";
    script.type = "text/javascript";
    script.onload = async () => {
      await castReceiverLog("Info", "Google Cast Receiver Framework loaded");
      setCastReceiverLoaded(true);
      configureReceiverCasting();
    };
    document.body.appendChild(script);
  }, [castReceiverLoaded, configureReceiverCasting, castReceiverLog]);
  const playMedia = useCallback(
    async (src: string) => {
      try {
        if (disableCasting || TESTING_ENABLED || mediaDiabled) {
          // play media directly without casting
          const audio = new Audio(src);
          audio.play().catch(async (e) => {
            await castReceiverLog(
              "Error",
              "Error playing media directly: " + e,
            );
          });
          return;
        }
        const context = cast.framework.CastReceiverContext.getInstance();
        const playerManager = context.getPlayerManager();
        const mediaInfo = new cast.framework.messages.MediaInformation();
        mediaInfo.contentId = src;
        mediaInfo.contentType = "audio/mpeg";
        const loadRequest = new cast.framework.messages.LoadRequestData();
        loadRequest.media = mediaInfo;
        loadRequest.autoplay = true;
        playerManager
          .load(loadRequest)
          .then(async () => {
            await castReceiverLog("Info", "Media loaded and playing");
          })
          .catch(async (e: any) => {
            setMediaDisabled(true);
            await castReceiverLog("Error", "Error loading media: " + e);
          });
      } catch (error) {
        setMediaDisabled(true);
        await castReceiverLog("Error", "Error playing media: " + error);
      }
    },
    [disableCasting, mediaDiabled, castReceiverLog],
  );
  useEffect(() => {
    if (castStatus === "CONNECTED") {
      const castMediaPlayer = document.createElement("cast-media-player");
      castMediaPlayer.style.display = "none";
      castMediaPlayer.id = "cast-media-player";
      document.body.appendChild(castMediaPlayer);
      castReceiverLog("Info", "Cast media player initialized");
    }
    castReceiverLog("Info", "Cast status updated to " + castStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [castStatus]);
  useEffect(() => {
    const onConnect = async () => {
      if (disableCasting || TESTING_ENABLED) {
        await castReceiverLog(
          "Info",
          "Casting is disabled, not sending session ID",
        );
        await sendSocketMessage({
          Type: "HostConnection",
          Payload: {
            SenderSessionId: "test-host-session-id",
          },
        });
        return;
      } else if (socketConnected && senderSessionId) {
        // This is step 2 of the connection process. Sender session ID is sent from
        // the receiver to authorize the host connection.
        await castReceiverLog(
          "Info",
          "WebSocket connected, authorizing host connection",
        );
        await sendSocketMessage({
          Type: "HostConnection",
          Payload: {
            SenderSessionId: senderSessionId,
          },
        });
        // Tell the sender we are connected so we don't try to connect again
        sendToCastSender({
          Type: "HostAuthorized",
        });
      }
    };
    !hostAuthorized && onConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    socketConnected,
    senderSessionId,
    sendSocketMessage,
    disableCasting,
    hostAuthorized,
    sendToCastSender,
  ]);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!disableCasting && !TESTING_ENABLED) {
        loadGoogleCastReceiverFramework();
      }
    }
  }, [
    loadGoogleCastReceiverFramework,
    disableCasting,
    castReceiverMessageHandler,
  ]);
  return {
    castConnected: castStatus === "CONNECTED",
    sendToCastSender,
    disableCasting,
    ping,
    senderSessionId,
    playMedia,
  };
}
